<template>
  <div
    class="zm-group-select"
    @click.stop="open"
    v-clickoutside="close"
    :class="appendCls"
    :style="{width:width}"
  >
    <zm-scrollbar
      ref="scrollbar"
      :wrap-style="inputWrapStyl"
    >
      <div
        class="zm-group-select-input"
        :class="{'is-focus': visible || isInputFocus}"
        ref="reference"
        @mouseenter="inputHovering = true"
        @mouseleave="inputHovering = false"
      >
        <div
          class="zm-group-select__tags"
          ref="tags"
          :style="{ 'max-width': inputWidth - 25 + 'px' }"
        >
          <span v-if="collapseTags && selected.length">
            <zm-tag
              closable
              class="zm-tag-fix"
              :hit="selected[0].hitState"
              :delete-text="t('tag.delete') + ' ' + selected[0][nameKey]"
              type="info"
              size="small"
              @close="deleteTag($event, selected[0])"
              disable-transitions
            >
              <span class="zm-group-select__tags-text">{{ selected[0][nameKey] }}
              </span>
            </zm-tag>
            <zm-tag
              class="zm-tag-fix"
              v-if="selected.length > 1"
              :closable="false"
              type="info"
              size="small"
              disable-transitions
            >
              <span class="zm-group-select__tags-text">+ {{ selected.length - 1 }}
              </span>
            </zm-tag>
          </span>
          <span v-if="!collapseTags">
            <zm-tag
              closable
              v-for="item in selected"
              :key="item.id"
              :hit="selected[0].hitState"
              :delete-text="t('tag.delete') + ' '+ item[nameKey]"
              type="info"
              size="small"
              @close="deleteTag($event, item)"
              disable-transitions
            >
              <span class="zm-group-select__tags-text">
                {{ item[nameKey] + (extraKey ? '(' + item[extraKey] + ')' : '')}}
              </span>
            </zm-tag>
          </span>

          <input
            :id="targetId"
            ref="input"
            type="text"
            v-bind="$attrs"
            class="zm-group-select__input"
            autocomplete="off"
            @focus="handleFocus"
            @blur="handleBlur"
            @keyup="managePlaceholder"
            @compositionstart="handleComposition"
            @compositionupdate="handleComposition"
            @compositionend="handleComposition"
            @keydown="handleKeyDown"
            :placeholder="currentPlaceholder"
            v-model="query"
            @input="debouncedQueryChange"
            :style="{ 'flex-grow': '1', width: 20 / (inputWidth - 32) + '%', 'max-width': inputWidth - 42 + 'px' }"
            :aria-label="currentPlaceholder"
            :aria-activedescendant="visible ? activedescendant: null"
            :aria-controls="listId"
            :aria-owns="listId"
            role="combobox"
            :aria-expanded="(visible).toString()"
            aria-haspopup="true"
            :readonly="readonly"
          >
        </div>
      </div>
    </zm-scrollbar>
    <transition
      name="zm-zoom-in-top"
    >
      <zm-group-select-dropdown
        ref="popper"
        :options="filterableOptions"
        :loading="loading"
        :width="width"
        :columns="columns"
        :no-data-text="noDataText || t('select.noData')"
        :append-to-body="false"
        :checkbox-column-width="checkboxColumnWidth"
        :no-divided="noDivided"
        :filterable="filterable"
        v-show="visible"
        @scroll-end="onDropdownScrollEnd"
        @change="onChange"
      >
      </zm-group-select-dropdown>
    </transition>
  </div>
</template>

<script>
  import '../../../i18n/select';
  import '../../../i18n/group-select';
  import debounce from 'throttle-debounce/debounce';
  import { valueEquals } from '../../utils/util';
  import ZmGroupSelectDropdown from './group-select-dropdown';
  import ZmTag from '../tag';
  import ZmScrollbar from '../scrollbar';
  import { isKorean } from '../../utils/shared';
  import Clickoutside from '../../utils/clickoutside';
  import Emitter from '../../mixins/emitter';
  import Locale from '../../mixins/locale';
  import { addClass, removeClass } from '../../utils/dom';
  import { getValueByPath, escapeRegexpString } from '../../utils/util';
  import AriaUtils from '../../utils/aria-utils';

  let _uid = 0;

  function uid() {
    return _uid++;
  }

  export default {
    name: 'ZmGroupSelect',

    components: {
      ZmGroupSelectDropdown,
      ZmTag,
      ZmScrollbar
    },

    mixins: [Emitter, Locale],

    directives: {
      Clickoutside
    },

    props: {
      value: {
        type: Array,
        required: true
      },
      placeholder: String,
      noDataText: String,
      debounce: {
        type: Number,
        default: 300
      },
      options: {
        type: Array,
        default() {
          return [];
        }
      },
      loading: Boolean,
      remoteMethod: Function,
      hasMore: Boolean,
      width: {
        type: String,
        default: '100%'
      },
      valueKey: {
        type: String,
        default: 'id'
      },
      nameKey: {
        type: String,
        required: true
      },
      extraKey: String,
      columns: {
        type: Array,
        default() {
          return [];
        }
      },
      maxLine: {
        type: Number,
        default: 0
      },
      queryWhenFocus: Boolean,
      filterable: Boolean,
      checkboxColumnWidth: {
        type: Number,
        default: 52
      },
      noDivided: Boolean,
      collapseTags: Boolean
    },

    data() {
      const placeholder = this.placeholder || this.t('group-select.placeholder');

      return {
        visible: false,
        isInputFocus: false,
        query: '',
        previousQuery: null,
        cachedPlaceHolder: placeholder,
        currentPlaceholder: placeholder,
        page: 1,
        selected: [],
        inputWidth: 0,
        inputWrapStyl: ''
      };
    },

    computed: {
      listId() {
        return `${this.targetId}-popup-list`;
      },
      targetId() {
        return 'group-select-' + uid();
      },
      activedescendant() {
        return this.$refs.popper.activedescendant;
      },
      appendCls() {
        const cls = [];
        if (this.isInputFocus && !this.visible) {
          cls.push('is-focus');
        }
        return cls.join(' ');
      },
      filterableOptions() {
        const options = this.options;
        if (!this.filterable) {
          return options;
        }
        const ret = [];
        const query = this.query;
        const columns = this.columns;

        if (query) {
          if (columns.length > 0) {
            const column = columns[0];
            options.forEach(option => {
              if (RegExp(escapeRegexpString(query), 'i').test(option[column.prop])) {
                ret.push(option);
              }
            });
          }
        } else {
          options.forEach(option => {
            ret.push(option);
          });
        }
        return ret;
      },
      readonly() {
        return !this.remoteMethod && !this.filterable;
      }
    },

    mounted() {
      this.resetInputWidth();
      this.setInputWrapStyl();
    },

    watch: {
      placeholder(val) {
        this.cachedPlaceHolder = this.currentPlaceholder = val;
      },
      value: {
        immediate: true,
        handler(val) {
          if (val.length > 0 || (this.$refs.input && this.query !== '')) {
            this.currentPlaceholder = '';
          } else {
            this.currentPlaceholder = this.cachedPlaceHolder;
          }
          this.setSelected();
          this.resetInputHeight();
        }
      },
      options() {
        this.syncSelected();
      },
      query() {
        this.managePlaceholder();
      },
      visible(val) {
        if (!val) {
          this.handleIconHide();
          this.broadcast('ZmGroupSelectDropdown', 'destroyPopper');
          this.query = '';
          this.previousQuery = null;
          this.activeIndex = -1;
        } else {
          if (typeof this.remoteMethod === 'function' && this.hasMore) {
            this.remoteMethod('', this.page);
          }
          this.handleIconShow();
          this.broadcast('ZmGroupSelectDropdown', 'updatePopper');
          this.$nextTick(() => {
            this.focus();
          });
        }
        this.resetInputHeight();
        this.$emit('visible-change', val);
        this.$nextTick(() => {
          this.managePlaceholder();
        });
      }
    },

    methods: {
      focus() {
        this.$refs.input.focus();
      },
      open() {
        this.visible = true;
      },
      close() {
        this.visible = false;
      },
      toggle() {
        this.visible = !this.visible;
      },
      handleIconHide() {
        let icon = this.$el.querySelector('.zm-input__icon');
        if (icon) {
          removeClass(icon, 'is-reverse');
        }
      },
      handleIconShow() {
        let icon = this.$el.querySelector('.zm-input__icon');
        if (icon) {
          addClass(icon, 'is-reverse');
        }
      },
      deleteTag(event, item) {
        const index = this.selected.indexOf(item);
        if (index > -1) {
          item.selected = false;
          const value = this.value.slice();
          value.splice(index, 1);
          this.$emit('input', value);
          this.emitChange(value);
          this.$emit('remove-tag', item.value);
          this.focus();
        }
        event.stopPropagation();
      },
      emitChange(val) {
        if (!valueEquals(this.value, val)) {
          this.$emit('change', val);
        }
      },
      resetInputHeight() {
        this.$nextTick(() => {
          // const input = this.$refs.referenceInner;
          // const size = 30;
          // input.style.height = this.selected.length === 0
          //   ? size + 'px'
          //   : 0;
          if (this.visible) {
            this.broadcast('ZmGroupSelectDropdown', 'updatePopper');
          }
        });
      },
      resetInputWidth() {
        this.inputWidth = this.$refs.reference.getBoundingClientRect().width;
      },
      managePlaceholder() {
        this.currentPlaceholder = this.value.length > 0 || this.$refs.input.value ? '' : this.cachedPlaceHolder;
      },
      handleFocus(event) {
        this.isInputFocus = true;
        this.$emit('focus', event);
        this.$nextTick(() => {
          if (this.queryWhenFocus) {
            this.handleQueryChange(this.query, true);
          }
        });
      },
      handleBlur(event) {
        this.isInputFocus = false;
        this.$emit('blur', event);
      },
      handleKeyDown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
            if (!this.visible) {
              this.open();
              event.preventDefault();
              event.stopPropagation();
            }
            break;
          case keys.down:
            if (!this.visible) {
              this.open();
            } else {
              this.$refs.popper.$el.focus();
            }
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.tab:
            if (!event.shiftKey) {
              this.close();
            }
            break;
          case keys.esc:
            this.close();
            this.focus();
            event.preventDefault();
            event.stopPropagation();
            break;
        }
      },
      handleComposition(event) {
        const text = event.target.value;
        if (event.type === 'compositionend') {
          this.isOnComposition = false;
          this.handleQueryChange(text);
        } else {
          const lastCharacter = text[text.length - 1] || '';
          this.isOnComposition = !isKorean(lastCharacter);
        }
      },
      handleQueryChange(val, userInput) {
        this.page = 1;
        if (userInput && !this.visible) {
          this.visible = true;
        }
        if (this.previousQuery === val || this.isOnComposition) {
          return;
        }
        this.previousQuery = val;
        this.resetInputHeight();
        if (typeof this.remoteMethod === 'function') {
          this.remoteMethod(val, this.page);
        } else if (this.filterable) {
          this.query = val;
        }
      },
      onDropdownScrollEnd() {
        if (this.hasMore) {
          this.page++;
          if (typeof this.remoteMethod === 'function') {
            this.remoteMethod(this.query, this.page);
          }
        }
      },
      onChange(selected) {
        const value = this.value.slice();
        if (Array.isArray(selected)) {
          // select or clear all
          if (selected.length === 0) {
            this.filterableOptions.forEach((item) => {
              const index = this.getValueIndex(value, item);
              if (index > -1) {
                value.splice(index, 1);
              }
            });
          } else {
            this.filterableOptions.forEach((item) => {
              const index = this.getValueIndex(value, item);
              if (index === -1) {
                value.push(item);
              }
            });
            this.$refs.scrollbar && this.$refs.scrollbar.scrollToEndY();
          }
        } else {
          const index = this.getValueIndex(value, selected);
          if (index > -1) {
            value.splice(index, 1);
          } else {
            value.push(selected);
            this.$refs.scrollbar && this.$refs.scrollbar.scrollToEndY();
          }
        }
        this.query = '';
        this.$emit('input', value);
      },
      setSelected() {
        const result = [];
        if (Array.isArray(this.value)) {
          this.value.forEach(val => {
            result.push(val);
          });
        }
        this.selected = result;
        this.syncSelected();
      },
      syncSelected() {
        this.filterableOptions.forEach((item) => {
          let selected = this.getValueIndex(this.selected, item) > -1;
          this.$set(item, 'selected', selected);
        });
      },
      setInputWrapStyl() {
        let style = 'height:auto;';
        if (this.maxLine > 0) {
          style += `max-height:${(this.maxLine * 30) + (this.maxLine) * 9}px;`;
        }

        this.inputWrapStyl = style;
      },
      getValueIndex(arr = [], value) {
        const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]';
        if (!isObject) {
          return arr.indexOf(value);
        } else {
          const valueKey = this.valueKey;
          let index = -1;
          arr.some((item, i) => {
            if (getValueByPath(item, valueKey) === getValueByPath(value, valueKey)) {
              index = i;
              return true;
            }
            return false;
          });
          return index;
        }
      }
    },

    created() {
      this.debouncedQueryChange = debounce(this.debounce, (e) => {
        this.handleQueryChange(e.target.value, true);
      });
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";
  @import "../../scss/common/transition";
  @import "../../scss/mixins/utils";

  @include b(group-select) {
    display: inline-block;
    position: relative;
    box-sizing: border-box;
    border: $--input-border;
    border-radius: $--input-border-radius;
    transition: $--border-transition-base;
    &:hover,
    &.is-focus {
      border-color: $--input-focus-border;
    }
    &.is-disabled {
      cursor: not-allowed;
      background-color: $--disabled-fill-base;
      border-color: $--disabled-border-base;

      .zm-group-select-input__inner1 {
        color: $--color-text-disabled;
      }
      .zm-group-select-span__inner1 {
        color: $--color-text-disabled;
      }
    }
    .zm-group-select-input {
      position: relative;
      display: block;
      align-items: center;
      font-size: $--font-size-base;

      padding: $--input-default-padding-vertical $--input-default-padding-horizontal;
      padding-right: $--input-default-padding-horizontal + 13;

      &:hover,
      &.is-focus {
        border-color: $--input-focus-border;
        .zm-group-select__caret {
          color: $--color-border-primary
        }
        [disabled] + .zm-group-select-toggle .zm-group-select__caret {
          color: inherit;
        }
      }
    }
    .zm-group-select-span__inner {
      display: block;
      vertical-align: middle;
      -ms-overflow-style: none;
      border: none;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      border-radius: $--input-border-radius;
      width: 100%;
      line-height: $--input-height - 2;
      height: $--input-height - 2;
      max-width: 100%;
      min-width: 60px;
      padding-top: 0;
      padding-bottom: 0;
      color: $--input-color;
      background-color: transparent;
      background-image: none;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        width: 0;
        height: 0;
      }

      &:focus {
        outline: none;
      }
      &::-ms-clear {
        display: none;
      }
      &.is-placeholder {
        color: $--color-text-placeholder;
      }
    }
    .zm-group-select-input {
      .zm-group-select-toggle {
        background-color: transparent;
        border: none;
        position: absolute;
        right: 8px;
        top: 50%;
        margin-top: -8px;
        padding: 0;
        line-height: 14px;
        border-radius: 50%;
        .zm-button__slot {
          line-height: 11px;
        }
        &:focus {
          outline: none;
          color: $--color-text-primary-hover;
          background-color: $--color-background-secondary-active;
          .zm-group-select__caret {
            color: inherit;
          }
        }
        &:hover {
          background-color: transparent;
          border-right-color: transparent;
        }
      }
      .zm-group-select__caret {
        text-align: center;
        color: $--select-input-color;
        font-size: $--font-size-base;
        width: 16px;
        height: 16px;
        line-height: 16px;
        transition: transform .3s;
        transform: rotateZ(180deg);

        @include when(reverse) {
          transform: rotateZ(0deg);
        }

        @include when(show-close) {
          font-size: $--select-font-size;
          text-align: center;
          transform: rotateZ(180deg);
          border-radius: $--border-radius-circle;
          color: $--select-input-color;
          transition: $--color-transition-base;

          &:hover {
            color: $--select-close-hover-color;
          }
        }
      }
    }

    @include e(input) {
      width: 100%;
      border: none;
      outline: none;
      padding: 0;
      margin-left: 6px;
      color: $--select-multiple-input-color;
      font-size: $--select-font-size;
      appearance: none;
      height: 30px;
      background-color: transparent;
    }

    @include e(tags) {
      display: flex;
      line-height: normal;
      white-space: normal;
      align-items: center;
      flex-wrap: wrap;
      margin-left: -6px;
      & > span {
        display: contents;
      }
    }

    .zm-tag {
      box-sizing: border-box;
      border-color: transparent;
      margin: 3px 6px 3px 0;

      &__close.zm-icon-close {
        background-color: $--color-text-placeholder;
        right: -7px;
        top: 0;
        color: $--color-white;

        &:hover {
          background-color: $--color-text-secondary;
        }

        &::before {
          display: block;
          transform: translate(0, .5px);
        }
      }

      &.is-hit {
        border-color: $--tag-info-color;
      }
    }
  }

  .zm-theme--dark {
    @include b(group-select) {
      border-color: #444b53;
    }
  }
</style>
